import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import type { PaginationState, BaseStoreState } from "../types"
import { type Transaction } from "@/src/services/credits"
import { useAuthStore } from "./auth.slice"
import { fetcher, apiUrls } from "@/src/api"

// Store state interface
export interface CreditsStoreState extends BaseStoreState {
    // History state
    historyTransactions: Transaction[]
    historyTotalCount: number
    historyPagination: PaginationState
    historyLoading: boolean
    historyError: string | null
}

// Store actions interface
export interface CreditsStoreActions {
    // History management
    fetchCreditsHistory: (page?: number, size?: number) => Promise<void>
    setHistoryPagination: (pagination: Partial<PaginationState>) => void
}

export type CreditsStore = CreditsStoreState & CreditsStoreActions

export const useCreditsStore = create<CreditsStore>()(
    immer((set, get) => ({
        // State
        loading: false,
        error: null,
        historyTransactions: [],
        historyTotalCount: 0,
        historyPagination: { page: 0, pageSize: 20 },
        historyLoading: false,
        historyError: null,

        // Actions
        fetchCreditsHistory: async (page?: number, size?: number) => {
            set((state) => {
                state.historyLoading = true
                state.historyError = null
            })

            try {
                const { historyPagination } = get()
                const { user } = useAuthStore.getState()

                if (!user?.activeWorkspace?.id) {
                    throw new Error("No active workspace found")
                }

                const targetPage = page ?? historyPagination.page
                const targetSize = size ?? historyPagination.pageSize

                const url = apiUrls.CREDITS_HISTORY()
                const response = await fetcher(`${url}?page=${targetPage}&size=${targetSize}`)

                set((state) => {
                    state.historyTransactions = response.data.transactions
                    state.historyTotalCount = response.data.totalCount
                    state.historyLoading = false
                    state.historyError = null
                })
            } catch (error) {
                set((state) => {
                    state.historyLoading = false
                    state.historyError = error instanceof Error ? error.message : "Failed to fetch credits history"
                })
            }
        },

        setHistoryPagination: (pagination: Partial<PaginationState>) => {
            set((state) => {
                state.historyPagination = { ...state.historyPagination, ...pagination }
            })
        },
    }))
)
